import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	forwardRef,
	Input,
	OnChanges,
	Output,
	SimpleChanges,
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { GeografiaNode } from "src/app/_core/models/geografia/geografia-node.model";
import { GeografiaService } from "src/app/_core/providers/geografia.service";

/** Nodo del arbol con estado de UI (expansion / carga perezosa). */
interface TreeNode {
	data: GeografiaNode;
	children: TreeNode[] | null; // null = aun no cargado
	expanded: boolean;
	loading: boolean;
}

/**
 * Control "Ubicacion": arbol de seleccion sobre Dama_Geografia, filtrado al pais del contrato.
 * - Carga perezosa (raiz por pais -> hijos por nodo).
 * - Solo permite seleccionar nodos del nivel minimo elegible o inferior (flag `seleccionable`).
 * - Pais sin subdivisiones -> autoselecciona el propio pais y avisa.
 * Ver ADR-0010.
 */
@Component({
	selector: "app-ubicacion-select",
	templateUrl: "./ubicacion-select.component.html",
	styleUrls: ["./ubicacion-select.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => UbicacionSelectComponent),
			multi: true,
		},
	],
	standalone: false,
})
export class UbicacionSelectComponent implements OnChanges, ControlValueAccessor {
	/** ISO-2 del pais del contrato; filtra todo el arbol. Al cambiar, se recarga la raiz. */
	@Input() codigoPais: string | null = null;
	@Input() disabled = false;

	/** Emite el nodo seleccionado (con nombre/ruta) para que el contenedor reaccione
	 * (p. ej. autocompletar coordenadas en el alta de centro mientras no sean manuales). */
	@Output() nodeSelected = new EventEmitter<GeografiaNode>();

	// Buscador (se dispara con botón/Enter, NO en cada tecla).
	searchText = "";
	resultados: GeografiaNode[] = [];
	buscando = false;
	busquedaHecha = false;

	root: TreeNode | null = null;
	loadingRoot = false;
	sinNiveles = false; // pais sin subdivisiones (solo el nodo pais)
	paisNoCargado = false; // el pais no existe en Dama_Geografia

	selectedId: number | null = null;
	selectedPath = "";

	private pendingValue: number | null = null;
	private onChange: (value: number | null) => void = () => {
		/* set por Angular */
	};
	private onTouched: () => void = () => {
		/* set por Angular */
	};

	constructor(
		private readonly geografiaService: GeografiaService,
		private readonly cdr: ChangeDetectorRef,
	) {}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes["codigoPais"]) {
			this.cargarRaiz();
		}
	}

	// ── ControlValueAccessor ────────────────────────────────────────────────
	writeValue(value: number | null): void {
		this.selectedId = value ?? null;
		this.pendingValue = value ?? null;
		if (this.root && value) {
			this.preexpandir(value);
		}
		this.cdr.markForCheck();
	}
	registerOnChange(fn: (value: number | null) => void): void {
		this.onChange = fn;
	}
	registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}
	setDisabledState(isDisabled: boolean): void {
		this.disabled = isDisabled;
		this.cdr.markForCheck();
	}

	// ── Carga ───────────────────────────────────────────────────────────────
	private cargarRaiz(): void {
		this.root = null;
		this.sinNiveles = false;
		this.paisNoCargado = false;
		if (!this.codigoPais) {
			this.cdr.markForCheck();
			return;
		}
		this.loadingRoot = true;
		this.cdr.markForCheck();
		this.geografiaService.getRaizPais(this.codigoPais).subscribe((node) => {
			this.loadingRoot = false;
			if (!node) {
				this.paisNoCargado = true;
				this.cdr.markForCheck();
				return;
			}
			this.root = {
				data: node,
				children: null,
				expanded: true,
				loading: false,
			};
			if (node.seleccionable && !node.tieneHijos) {
				// Pais sin subdivisiones: el unico elegible es el propio pais.
				this.sinNiveles = true;
				const emit = this.selectedId !== node.id;
				this.seleccionar(this.root, emit);
			} else {
				this.toggle(this.root, true);
			}
			if (this.pendingValue) {
				this.preexpandir(this.pendingValue);
			}
			this.cdr.markForCheck();
		});
	}

	toggle(node: TreeNode, forceOpen = false): void {
		if (!node.data.tieneHijos) {
			return;
		}
		node.expanded = forceOpen ? true : !node.expanded;
		if (node.expanded && node.children === null) {
			node.loading = true;
			this.cdr.markForCheck();
			this.geografiaService.getHijos(node.data.id).subscribe((hijos) => {
				node.loading = false;
				node.children = (hijos ?? []).map((h) => ({
					data: h,
					children: null,
					expanded: false,
					loading: false,
				}));
				this.cdr.markForCheck();
			});
		}
	}

	/** Click en una fila: selecciona si es elegible; si no, alterna la expansion. */
	seleccionar(node: TreeNode, emit = true): void {
		if (this.disabled) {
			return;
		}
		if (!node.data.seleccionable) {
			this.toggle(node);
			return;
		}
		this.selectedId = node.data.id;
		this.selectedPath = this.formatPath(node.data.pathNombre);
		if (emit) {
			this.onChange(this.selectedId);
			this.onTouched();
			this.nodeSelected.emit(node.data);
		}
		this.cdr.markForCheck();
	}

	// ── Buscador (se dispara con botón/Enter) ───────────────────────────────
	buscar(): void {
		const t = (this.searchText || "").trim();
		if (!this.codigoPais || t.length < 2) {
			return;
		}
		this.buscando = true;
		this.busquedaHecha = false;
		this.cdr.markForCheck();
		this.geografiaService.buscar(this.codigoPais, t).subscribe((res) => {
			this.resultados = res || [];
			this.buscando = false;
			this.busquedaHecha = true;
			this.cdr.markForCheck();
		});
	}

	limpiarBusqueda(): void {
		this.searchText = "";
		this.resultados = [];
		this.busquedaHecha = false;
		this.cdr.markForCheck();
	}

	/** Ruta legible de un resultado (sin el continente), para mostrar el contexto. */
	rutaResultado(node: GeografiaNode): string {
		return this.formatPath(node.pathNombre);
	}

	/** Selecciona un resultado: fija el valor, emite y revela el nodo en el árbol. */
	seleccionarResultado(node: GeografiaNode): void {
		if (this.disabled || !node?.seleccionable) {
			return;
		}
		this.selectedId = node.id;
		this.selectedPath = this.formatPath(node.pathNombre);
		this.onChange(this.selectedId);
		this.onTouched();
		this.nodeSelected.emit(node);
		this.resultados = [];
		this.busquedaHecha = false;
		this.searchText = "";
		if (this.root) {
			this.preexpandir(node.id);
		}
		this.cdr.markForCheck();
	}

	// ── Preseleccion (expandir hasta una seleccion existente) ────────────────
	private preexpandir(id: number): void {
		this.geografiaService.getRuta(id).subscribe((ruta) => {
			if (!ruta || !ruta.length || !this.root) {
				return;
			}
			const last = ruta[ruta.length - 1];
			this.selectedId = id;
			this.selectedPath = this.formatPath(last.pathNombre);
			this.expandirCadena(this.root, ruta, 1); // ruta[0] = pais = root
			this.pendingValue = null;
			this.cdr.markForCheck();
		});
	}

	private expandirCadena(node: TreeNode, ruta: GeografiaNode[], idx: number): void {
		if (idx >= ruta.length) {
			return;
		}
		node.expanded = true;
		const continuar = (): void => {
			const child = node.children?.find((c) => c.data.id === ruta[idx].id);
			if (child) {
				this.expandirCadena(child, ruta, idx + 1);
			}
		};
		if (node.children === null && node.data.tieneHijos) {
			node.loading = true;
			this.geografiaService.getHijos(node.data.id).subscribe((hijos) => {
				node.loading = false;
				node.children = (hijos ?? []).map((h) => ({
					data: h,
					children: null,
					expanded: false,
					loading: false,
				}));
				this.cdr.markForCheck();
				continuar();
			});
		} else {
			continuar();
		}
	}

	/** Ruta legible sin el continente (primer segmento). */
	private formatPath(pathNombre: string): string {
		if (!pathNombre) {
			return "";
		}
		const parts = pathNombre.split("/");
		return (parts.length > 1 ? parts.slice(1) : parts).join(" / ");
	}
}
